// A template form's AI Actor tile (live's `ImageSlotTile` with the actor menu, spec G14-templates):
// empty, its icon and label, live's menu (From Library, Add AI Actor) and its form's tooltip;
// while "Add AI Actor" makes one, a loader (failed: an x); once picked, the portrait with an x.
// Your Personal Clone's takes an upload alone (live's opens the file chooser at once).
import { CloseButton, FileButton, Image, Loader, Menu, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { LibraryBig, SmilePlus, Sparkles } from "lucide-react";
import { useEffect, useRef } from "react";

import type { PickedActor } from "../../../platform/actors/picked";
import type { Making } from "../../../platform/actors/use-makings";
import { UploadState, useImageUpload } from "../../../platform/media/image-upload";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import classes from "./forms.module.css";

/** Live's image types for an uploaded portrait (Your Personal Clone's rules). */
const PORTRAIT_TYPES = "image/jpeg,image/png,image/webp";

export interface ActorTileProps {
  /** The actor shown: a pick, or a template example's portrait (issue 749). */
  actor: Pick<PickedActor, "name" | "imageUrl"> | null;
  /** The actor "Add AI Actor" is making, or none. */
  making: Making | null;
  /** The form's own tooltip on the empty tile. */
  tip: string;
  onFromLibrary: () => void;
  /** Live's "Add AI Actor"; none while no model makes actors. */
  onMakeActor: (() => void) | null;
  onRemove: () => void;
  onDropMaking: () => void;
  /** Greyed, as live's while the form is busy (its voiceover made, its run started). */
  disabled?: boolean;
}

/** Live's AI Actor tile with its menu. */
export function ActorTile(props: ActorTileProps) {
  const { actor, making, tip, onFromLibrary, onMakeActor, onRemove, onDropMaking } = props;
  const disabled = props.disabled === true;
  if (actor !== null) {
    return (
      <span className={classes.actorTile}>
        {actor.imageUrl === null ? <Text size="xs">{actor.name}</Text> : <Image src={actor.imageUrl} alt={actor.name} />}
        <CloseButton
          size="xs"
          className={classes.actorRemove}
          aria-label="Remove the AI Actor"
          disabled={disabled}
          onClick={onRemove}
        />
      </span>
    );
  }
  if (making !== null) {
    return (
      <span className={classes.actorTile} aria-busy={!making.failed}>
        {making.failed ? (
          <CloseButton size="xs" aria-label="Remove the failed AI Actor" onClick={onDropMaking} />
        ) : (
          <Loader size={20} aria-label="Making the AI Actor" />
        )}
      </span>
    );
  }
  return (
    <Menu position="top-start">
      <Menu.Target>
        <Tooltip label={tip} multiline maw={280} withArrow>
          <UnstyledButton className={classes.actorTile} aria-label="AI Actor" disabled={disabled}>
            <SmilePlus size={20} strokeWidth={1.5} aria-hidden />
            <Text size="xs">AI Actor</Text>
          </UnstyledButton>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<LibraryBig size={16} strokeWidth={1.5} />} onClick={onFromLibrary}>
          From Library
        </Menu.Item>
        <Menu.Item
          leftSection={<Sparkles size={16} strokeWidth={1.5} />}
          disabled={onMakeActor === null}
          onClick={() => onMakeActor?.()}
        >
          Add AI Actor
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export interface PortraitTileProps {
  teamId: string;
  brandId: string;
  value: PickedImage | null;
  tip: string;
  onChange: (value: PickedImage | null) => void;
  /** Greyed, as live's while the form is busy. */
  disabled?: boolean;
}

/**
 * Your Personal Clone's AI Actor tile (live's upload-only `ImageSlotTile`): a click opens the
 * file chooser, the photo goes to the library, and the tile shows it with an x.
 */
export function PortraitTile({ teamId, brandId, value, tip, onChange, disabled = false }: PortraitTileProps) {
  const resetChooser = useRef<() => void>(null);
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  const upload = useImageUpload({
    teamId,
    brandId,
    onUploaded: (asset) =>
      onChange({
        assetId: asset.id,
        url: asset.urls.thumb ?? asset.urls.original ?? null,
        name: asset.name,
        width: asset.width,
        height: asset.height,
        durationMs: asset.duration_ms,
      }),
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  if (value !== null) {
    return (
      <span className={classes.actorTile}>
        {value.url === null ? <Text size="xs">{value.name}</Text> : <Image src={value.url} alt={value.name} />}
        <CloseButton
          size="xs"
          className={classes.actorRemove}
          aria-label="Remove the AI Actor"
          disabled={disabled}
          onClick={() => onChange(null)}
        />
      </span>
    );
  }
  return (
    <>
      <FileButton
        accept={PORTRAIT_TYPES}
        resetRef={resetChooser}
        onChange={(file) => {
          void upload.send(file);
          resetChooser.current?.();
        }}
        inputProps={{ "aria-label": "Choose a photo of yourself to upload" }}
      >
        {(chooser) => (
          <Tooltip label={tip} multiline maw={280} withArrow>
            <UnstyledButton
              className={classes.actorTile}
              aria-label="AI Actor"
              aria-busy={upload.pending}
              disabled={disabled}
              onClick={chooser.onClick}
            >
              {upload.pending ? <Loader size={20} aria-hidden /> : <SmilePlus size={20} strokeWidth={1.5} aria-hidden />}
              <Text size="xs">AI Actor</Text>
            </UnstyledButton>
          </Tooltip>
        )}
      </FileButton>
      <UploadState upload={upload} />
    </>
  );
}
